import { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { CameraIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { verificationApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';

interface SelfieStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function SelfieStep({ onNext, onBack }: SelfieStepProps) {
  const webcamRef = useRef<Webcam>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const { requestId, selfieDocumentId, setSelfieDocumentId, setStep } = useVerificationStore();

  const videoConstraints = {
    width: 480,
    height: 640,
    facingMode: 'user',
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
    }
  }, []);

  const retake = () => {
    setCapturedImage(null);
    setSelfieDocumentId('');
  };

  const uploadSelfie = async () => {
    if (!capturedImage || !requestId) return;

    setError(null);
    setIsUploading(true);

    try {
      // Convert base64 to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });

      const uploadResponse = await verificationApi.uploadDocument(requestId, file, 'Selfie');
      setSelfieDocumentId(uploadResponse.data.documentId);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to upload selfie');
    } finally {
      setIsUploading(false);
    }
  };

  const handleContinue = () => {
    if (!selfieDocumentId) {
      setError('Please capture and upload your selfie');
      return;
    }
    setStep('review');
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Take a Selfie</h2>
        <p className="text-sm text-gray-600 mt-1">
          Please take a clear photo of your face. This will be compared with your ID document.
        </p>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="space-y-4">
        {!selfieDocumentId ? (
          <div className="flex flex-col items-center">
            <div className="relative bg-black rounded-lg overflow-hidden">
              {!capturedImage ? (
                <>
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    onUserMedia={() => setCameraReady(true)}
                    onUserMediaError={() => setError('Unable to access camera. Please allow camera permissions.')}
                    className="rounded-lg"
                  />
                  {/* Face guide overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-64 border-2 border-white border-dashed rounded-full opacity-50" />
                  </div>
                </>
              ) : (
                <img src={capturedImage} alt="Captured selfie" className="rounded-lg" />
              )}
            </div>

            <div className="flex space-x-4 mt-4">
              {!capturedImage ? (
                <button
                  type="button"
                  onClick={capture}
                  disabled={!cameraReady}
                  className="btn-primary flex items-center"
                >
                  <CameraIcon className="h-5 w-5 mr-2" />
                  Capture Photo
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={retake}
                    className="btn-secondary flex items-center"
                  >
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Retake
                  </button>
                  <button
                    type="button"
                    onClick={uploadSelfie}
                    disabled={isUploading}
                    className="btn-primary flex items-center"
                  >
                    {isUploading ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        Use This Photo
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="relative">
              <img
                src={capturedImage || ''}
                alt="Uploaded selfie"
                className="rounded-lg max-w-xs"
              />
              <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="mt-4 text-green-600 font-medium">Selfie uploaded successfully!</p>
            <button
              type="button"
              onClick={retake}
              className="mt-2 text-sm text-primary-600 hover:text-primary-500"
            >
              Take a different photo
            </button>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Tips for a good selfie:</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Position your face within the oval guide</li>
            <li>Ensure good, even lighting on your face</li>
            <li>Remove glasses and hats if possible</li>
            <li>Keep a neutral expression</li>
            <li>Look directly at the camera</li>
          </ul>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button type="button" onClick={onBack} className="btn-secondary">
          Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          className="btn-primary"
          disabled={!selfieDocumentId}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
